import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toastError, toastSuccess } from '@/lib/toast';
import { useMutation } from 'react-query';
import { NetworkWanDTO } from '@webip/shared/src/dto/appliance';
import VBox from '@/components/layout/VBox';
import HBox from '@/components/layout/HBox';
import Card from '@/components/layout/Card';
import Botao from '@/components/Botao';
import { extractApiError } from '@/lib/apiError';
import styles from './LoadBalancingPanel.module.css';

// TOTAL de unidades de balanceamento distribuídas entre os links primários.
// Cada unidade ~10% do tráfego; a soma é sempre TOTAL (= 100%).
const TOTAL = 10;
// Mínimo por link (1 unidade = 10%). Um primário nunca fica em 0%, senão deixa
// de receber tráfego balanceado e vira efetivamente só failover.
const MIN = 1;

/** Peso salvo (>= 0) de um link primário por id, ou 0 se ausente. */
function pesosByIdStored(primaries: NetworkWanDTO[], id: number): number {
  const w = primaries.find(p => p.id === id);
  return Math.max(0, w?.peso ?? 0);
}

/**
 * Distribui `amount` unidades entre `ids` proporcionalmente aos pesos atuais em
 * `base`, garantindo no mínimo `min` por item, usando arredondamento de maior
 * resto para que a soma seja exatamente `amount`. Quando a base soma zero,
 * divide igualmente. Pressupõe `amount >= ids.length * min` (o chamador limita
 * o valor arrastado para que isso valha).
 */
function distribute(
  ids: number[],
  amount: number,
  base: Record<number, number>,
  min = 0,
): Record<number, number> {
  const out: Record<number, number> = {};
  if (ids.length === 0) return out;
  // reserva `min` para cada item e distribui só o excedente proporcionalmente
  const extra = Math.max(0, amount - ids.length * min);
  const baseSum = ids.reduce((a, id) => a + (base[id] ?? 0), 0);
  const raw = ids.map(id => ({
    id,
    v: baseSum > 0 ? ((base[id] ?? 0) / baseSum) * extra : extra / ids.length,
  }));
  let assigned = 0;
  const floored = raw.map(r => {
    const f = Math.floor(r.v);
    out[r.id] = min + f;
    assigned += f;
    return { id: r.id, frac: r.v - f };
  });
  // distribui o resto (extra - assigned) aos maiores fracionários
  floored.sort((a, b) => b.frac - a.frac);
  let leftover = extra - assigned;
  for (let i = 0; i < floored.length && leftover > 0; i++, leftover--) {
    out[floored[i].id] += 1;
  }
  return out;
}

/**
 * Painel de balanceamento de carga: aparece quando há 2+ WANs primárias.
 * Cada link recebe uma fatia percentual do tráfego (somando 100%). Mover um
 * slider redistribui os demais para manter o total. As fatias são salvas como
 * unidades de peso (0-10); o adapter-mk reduz pelo MDC e gera N regras PCC.
 */
export const LoadBalancingPanel = ({
  primaries,
  applianceId,
  onSaved,
}: {
  primaries: NetworkWanDTO[];
  applianceId?: number;
  onSaved: () => void;
}) => {
  const ids = useMemo(
    () => primaries.filter(w => w.id != null).map(w => w.id as number),
    [primaries],
  );

  // peso local por WAN id (0-10 unidades), normalizado para somar TOTAL.
  // Estado inicial (e sempre que a lista de primários muda) deriva dos pesos
  // salvos; se eles não somarem TOTAL (dados legados/novos links), normaliza.
  const [pesos, setPesos] = useState<Record<number, number>>({});
  const baselineRef = useRef<Record<number, number>>({});

  // `primaries` é recriado a cada render do pai (filter inline) — depender da
  // referência reinicializaria os sliders a cada refetch e apagaria edições não
  // salvas. Por isso a inicialização observa só uma assinatura estável de
  // id:peso, reinicializando apenas quando os dados realmente mudam.
  const signature = ids.map(id => `${id}:${pesosByIdStored(primaries, id)}`).join('|');
  useEffect(() => {
    const stored: Record<number, number> = {};
    for (const id of ids) stored[id] = pesosByIdStored(primaries, id);
    const sum = ids.reduce((a, id) => a + stored[id], 0);
    // aceita os pesos salvos só se já somam TOTAL e respeitam o mínimo; senão
    // redistribui TOTAL na proporção deles (ou igualmente se soma 0)
    const ok = sum === TOTAL && ids.every(id => stored[id] >= MIN);
    const normalized = ok
      ? stored
      : distribute(ids, TOTAL, sum > 0 ? stored : {}, MIN);
    baselineRef.current = normalized;
    setPesos(normalized);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  // Move o link `id` para `target` unidades e redistribui o restante entre os
  // demais, mantendo a soma em TOTAL.
  const handleSlide = (id: number, target: number) => {
    const others = ids.filter(o => o !== id);
    if (others.length === 0) return;
    // limita o valor para sobrar pelo menos MIN a cada um dos demais
    const maxV = TOTAL - others.length * MIN;
    const clamped = Math.max(MIN, Math.min(maxV, Math.round(target)));
    const rest = distribute(others, TOTAL - clamped, pesos, MIN);
    setPesos({ [id]: clamped, ...rest });
  };

  const dirty = useMemo(
    () => ids.some(id => (pesos[id] ?? 0) !== (baselineRef.current[id] ?? 0)),
    [ids, pesos],
  );

  const mutSave = useMutation({
    mutationFn: async () => {
      await Promise.all(
        primaries
          .filter(w => w.id != null)
          .map(w =>
            axios.put(`/api/rede_wan/${w.id}`, {
              ...w,
              peso: pesos[w.id!] ?? 0,
              appliance_id: applianceId,
            }),
          ),
      );
    },
    onSuccess: () => {
      toastSuccess('Balanceamento de carga salvo com sucesso!');
      baselineRef.current = pesos;
      onSaved();
    },
    onError: (error: unknown) => {
      const { message } = extractApiError(error, 'Erro ao salvar balanceamento');
      toastError(message);
    },
  });

  if (primaries.length < 2) return null;

  return (
    <VBox className={styles.container} style={{ gap: '16px' }}>
      <h3 style={{ marginBottom: '8px', fontSize: '18px', fontWeight: '600' }}>
        Balanceamento de carga
      </h3>
      <p style={{ color: 'var(--color-muted)', fontSize: '14px', margin: 0 }}>
        Defina o percentual de tráfego de cada link primário. Os valores somam
        sempre 100% — ao mover um link, os demais se ajustam. Dê mais % aos links
        com maior banda.
      </p>

      {/* Wrapper div: Card usa flex:1 0 0 (basis 0) e colapsaria a 0 de altura
          como filho direto do VBox (flex-column). O div o isola desse contexto. */}
      <div>
        <Card>
          <Card.Body>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {primaries.map(wan => {
                const peso = wan.id != null ? pesos[wan.id] ?? 0 : 0;
                const pct = peso * (100 / TOTAL);
                // thumb limitado para os demais manterem o mínimo; a barra de
                // preenchimento (--fill) é relativa ao intervalo [MIN, sliderMax]
                const sliderMax = TOTAL - (ids.length - 1) * MIN;
                const fillRange = sliderMax - MIN;
                const fill = fillRange > 0 ? ((peso - MIN) / fillRange) * 100 : 0;
                return (
                  <div
                    key={wan.id}
                    style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}
                  >
                    <HBox style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <strong style={{ fontSize: '14px' }}>{wan.nome}</strong>
                      <span style={{ fontSize: '14px', color: 'var(--color-muted)' }}>
                        {pct}%
                      </span>
                    </HBox>
                    <input
                      type="range"
                      className={styles.slider}
                      min={MIN}
                      max={sliderMax}
                      step={1}
                      value={peso}
                      onChange={e =>
                        wan.id != null && handleSlide(wan.id, +e.target.value)
                      }
                      style={{ ['--fill' as string]: `${fill}%` } as React.CSSProperties}
                    />
                  </div>
                );
              })}
            </div>
          </Card.Body>
        </Card>
      </div>

      <HBox style={{ justifyContent: 'flex-end', alignItems: 'center' }}>
        <Botao
          variant="primary"
          disabled={!dirty || mutSave.isLoading}
          onClick={() => mutSave.mutate()}
        >
          Salvar pesos
        </Botao>
      </HBox>
    </VBox >
  );
};
